import type { CustomerDocument, DocumentSourceKind, ExtractionResult } from '@protoss/shared';
import { normalizeFieldKey } from '@protoss/shared';
import {
  ArchonIcon,
  Button,
  CheckIcon,
  ChevronDownIcon,
  Cross1Icon,
  Dialog,
  DropdownMenu,
  FileTextIcon,
  IconSize,
  ListBulletIcon,
  MagnifyingGlassIcon,
  ReloadIcon,
  TextField,
  Tooltip,
} from '@protoss/ui';
import * as React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AppNav } from '../components/AppNav';
import { ConfirmDeleteDialog } from '../components/ConfirmDeleteDialog';
import { AppShell, MainContentShell, type RightRailItem } from '../components/shell';
import { useDirtyStateContext } from '../contexts/DirtyStateContext';
import { useLayout, useLayoutConfig } from '../contexts/LayoutContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { CustomerDetailPage } from '../features/customers/components/CustomerDetailPage';
import { CustomerListPage } from '../features/customers/components/CustomerListPage';
import {
  AttributeDescriptionsPanel,
  AttributeDesignerPanel,
  CustomerDocumentAddPanel,
  CustomerDocumentsPanel,
  CustomerDocumentViewPanel,
  CustomerImportArchonPanel,
  CustomerImportCsvPanel,
  type CustomerRailAttribute,
  type CustomerRailState,
  type NewAttributeDraft,
} from '../features/customers/components/CustomerRightRailPanels';
import type { DocumentAddDraft } from '../features/customers/hooks/useCustomerDocuments';
import { customersApi } from '../features/customers/services/customersApi';
import { useTemporaryRightPaneWidth } from '../hooks/useTemporaryRightPaneWidth';
import { useWorkspaceAdmin } from '../hooks/useWorkspaceAdmin';
import './home-page.css';

function HeaderSearchControl({
  value,
  onChange,
  showDatabaseAction,
  onSearchDatabase,
  searchingDatabase,
}: {
  value: string;
  onChange: (next: string) => void;
  showDatabaseAction: boolean;
  onSearchDatabase: () => void;
  searchingDatabase: boolean;
}) {
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (value.trim()) {
      setOpen(true);
    }
  }, [value]);

  React.useEffect(() => {
    if (!open) return;
    const handlePointerDown = (event: MouseEvent) => {
      if (!rootRef.current) return;
      if (rootRef.current.contains(event.target as Node)) return;
      if (!value.trim()) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [open, value]);

  return (
    <div
      ref={rootRef}
      className={`customer-header-search${open ? ' customer-header-search--open' : ''}`}
    >
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="customer-header-search__toggle"
        onClick={() => {
          setOpen(true);
          // Defer so the input is visible before focus
          window.setTimeout(() => inputRef.current?.focus(), 0);
        }}
        aria-label="Search customers"
      >
        <MagnifyingGlassIcon width={14} height={14} aria-hidden="true" />
      </Button>

      <div className="customer-header-search__input-wrap">
        <span className="customer-header-search__leading" aria-hidden="true">
          {searchingDatabase ? (
            <ReloadIcon width={14} height={14} className="customer-header-search__spinner" />
          ) : (
            <MagnifyingGlassIcon width={14} height={14} />
          )}
        </span>
        <TextField
          ref={inputRef}
          className="customer-header-search__field"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Search name, email, company"
          aria-label="Search customers by name, email, or company"
          autoFocus={open}
        />
        <button
          type="button"
          className="customer-header-search__clear"
          onClick={() => onChange('')}
          aria-label="Clear search"
        >
          <Cross1Icon width={12} height={12} aria-hidden="true" />
        </button>
      </div>

      {open && showDatabaseAction && (
        <button
          type="button"
          className="customer-header-search__database-action"
          onClick={onSearchDatabase}
          disabled={searchingDatabase}
        >
          Search database
        </button>
      )}
    </div>
  );
}

/**
 * CustomersPage - workspace-scoped customer/persona research surface for the Product module.
 *
 * This page intentionally does not require a project. Product artifacts are
 * workspace-owned first and may optionally be associated with a project later.
 */
function CustomersPageContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ customerUuid?: string }>();
  const { workspace, isLoadingWorkspace, activeModule } = useWorkspace();
  const isWorkspaceAdmin = useWorkspaceAdmin(workspace);
  const { setLayoutConfig, setRightPaneVisible, setRightPaneMode } = useLayout();
  const { saveWidth: savePaneWidth, restoreWidth: restorePaneWidth } = useTemporaryRightPaneWidth();

  // No context pane on this page, and - crucially - guarantee the right rail/pane
  // state this page drives below is reset on unmount so it never leaks onto the
  // next page. The mode-specific logic below layers on top during the page's life.
  useLayoutConfig({ showContextPane: false });

  const currentPath = location.pathname;
  const customersBasePath = `${activeModule?.defaultRoute ?? '/po'}/customers`;
  const selectedCustomerUuid = params.customerUuid ?? null;
  const creatingCustomer = currentPath === `${customersBasePath}/create`;
  const detailEditing = Boolean(
    selectedCustomerUuid && currentPath === `${customersBasePath}/${selectedCustomerUuid}/edit`
  );
  const detailViewing = Boolean(selectedCustomerUuid && !detailEditing);

  const [selectedCustomerName, setSelectedCustomerName] = React.useState<string | null>(null);

  // ── Import Archon state - declared early because useLayoutEffect below depends on it ──
  const [importArchonOpen, setImportArchonOpen] = React.useState(false);

  // ── Import CSV right-rail state ──────────────────────────────────────────────
  const [importCsvOpen, setImportCsvOpen] = React.useState(false);
  const csvFileInputRef = React.useRef<HTMLInputElement>(null);
  const [csvImportFile, setCsvImportFile] = React.useState<File | null>(null);
  const [csvPreviewData, setCsvPreviewData] = React.useState<{
    filename: string;
    totalRows: number;
    newRows: number;
    duplicateRows: number;
    noContactRows: number;
    mappedColumns: number;
    mappings: Array<{
      csvColumn: string;
      fieldKey: string;
      fieldLabel: string;
    }>;
    rawMappings: unknown[];
    preview: Array<{
      name: string;
      email: string | null;
      phone: string | null;
      linkedin: string | null;
      status: 'new' | 'duplicate' | 'no_contact';
      matchedBy: 'email' | 'linkedin' | null;
      extra: Array<{ fieldKey: string; label: string; value: string }>;
    }>;
  } | null>(null);
  const [csvImportInProgress, setCsvImportInProgress] = React.useState(false);
  const [importPanel, setImportPanel] = React.useState<{
    status: 'polling' | 'review';
    runId: number | null;
    results?: ExtractionResult[];
  } | null>(null);
  const [importAccepted, setImportAccepted] = React.useState<ExtractionResult[]>([]);
  const [importCreateInProgress, setImportCreateInProgress] = React.useState(false);
  const [importCreatedUuid, setImportCreatedUuid] = React.useState<string | null>(null);
  const [importDuplicate, setImportDuplicate] = React.useState<{
    matchedBy: 'email' | 'linkedin';
    customerName: string | null;
    customerUuid: string | null;
  } | null>(null);
  /** Current text in the import editor - kept in parent so the header button can read it */
  const [importText, setImportText] = React.useState('');
  /** Title entered by the user in the import panel */
  const [importTitle, setImportTitle] = React.useState('');

  // Clear the customer name when navigating away from a selected customer
  React.useEffect(() => {
    if (!selectedCustomerUuid) setSelectedCustomerName(null);
  }, [selectedCustomerUuid]);

  // Enable right rail for create/edit/view modes.
  // Create/edit opens descriptions by default; detail-view keeps it collapsed.
  // useLayoutEffect (not useEffect) ensures layout state is set synchronously
  // before the browser paints, preventing a flash where the pane appears
  // without the rail strip on navigation.
  React.useLayoutEffect(() => {
    const active =
      creatingCustomer || detailEditing || detailViewing || importArchonOpen || importCsvOpen;
    setLayoutConfig({ showRightRail: active });

    if (creatingCustomer || detailEditing) {
      setRightPaneMode('attribute-descriptions');
      setRightPaneVisible(true);
    } else if (detailViewing) {
      setRightPaneMode('attribute-descriptions');
      setRightPaneVisible(false);
      setAddingAttribute(false);
      setEditingAttribute(null);
    } else if (importArchonOpen) {
      setRightPaneMode('import-archon');
      setRightPaneVisible(true);
    } else if (importCsvOpen) {
      setRightPaneMode('import-csv');
      setRightPaneVisible(true);
    } else {
      setRightPaneVisible(false);
      setRightPaneMode(null);
      setAddingAttribute(false);
      setEditingAttribute(null);
    }
  }, [
    creatingCustomer,
    detailEditing,
    detailViewing,
    importArchonOpen,
    importCsvOpen,
    setLayoutConfig,
    setRightPaneMode,
    setRightPaneVisible,
  ]);

  // Fetch attribute template when entering create / edit / detail-view mode (or import)
  React.useEffect(() => {
    if (!(creatingCustomer || detailEditing || detailViewing || importArchonOpen || importCsvOpen))
      return;
    let cancelled = false;
    void (async () => {
      try {
        const { template } = await customersApi.getTemplate();
        if (!cancelled) setRailState((cur) => ({ ...cur, template }));
      } catch {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [creatingCustomer, detailEditing, detailViewing, importArchonOpen, importCsvOpen]);

  const [hasCustomers, setHasCustomers] = React.useState(false);
  const [formState, setFormState] = React.useState({
    submitting: false,
    canSubmit: false,
    saveSucceeded: false,
  });
  const [refreshKey, setRefreshKey] = React.useState(0);
  const [pendingAttributeDraft, setPendingAttributeDraft] =
    React.useState<NewAttributeDraft | null>(null);
  const [deletedAttributeSignal, setDeletedAttributeSignal] = React.useState<{
    fieldKey: string;
    name: string;
    nonce: number;
  } | null>(null);
  const [railState, setRailState] = React.useState<CustomerRailState>({
    template: null,
    attributes: [],
  });
  const [addingAttribute, setAddingAttribute] = React.useState(false);
  const [canSubmitAttributeCreate, setCanSubmitAttributeCreate] = React.useState(false);
  const [editingAttribute, setEditingAttribute] = React.useState<CustomerRailAttribute | null>(
    null
  );

  type SelectedCustomer = { uuid: string; name: string; email: string | null };
  const [selectedCustomers, setSelectedCustomers] = React.useState<SelectedCustomer[]>([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
  const [cancelExtractionConfirmOpen, setCancelExtractionConfirmOpen] = React.useState(false);
  const [importCancelConfirmOpen, setImportCancelConfirmOpen] = React.useState(false);
  const [deleteInProgress, setDeleteInProgress] = React.useState(false);
  const [customerSearchQuery, setCustomerSearchQuery] = React.useState('');
  const [databaseSearchTrigger, setDatabaseSearchTrigger] = React.useState(0);
  const [showDatabaseSearchAction, setShowDatabaseSearchAction] = React.useState(false);
  const [searchingDatabase, setSearchingDatabase] = React.useState(false);

  // ── Documents rail state ───────────────────────────────────────────────────
  const [documentAddMode, setDocumentAddMode] = React.useState<'paste' | 'upload' | null>(null);
  const [canSubmitDocumentAdd, setCanSubmitDocumentAdd] = React.useState(false);
  const [documentSaveState, setDocumentSaveState] = React.useState<'idle' | 'saving' | 'saved'>(
    'idle'
  );
  const [viewingDocumentUuid, setViewingDocumentUuid] = React.useState<string | null>(null);
  const [viewingDocumentKind, setViewingDocumentKind] = React.useState<DocumentSourceKind | null>(
    null
  );
  const [viewingDocumentChunkStatus, setViewingDocumentChunkStatus] = React.useState<
    'pending' | 'chunked' | 'failed' | null
  >(null);
  const [lastAddedDocument, setLastAddedDocument] = React.useState<CustomerDocument | null>(null);
  const [editingDocumentUuid, setEditingDocumentUuid] = React.useState<string | null>(null);
  const [extractionPanel, setExtractionPanel] = React.useState<{
    docUuid: string;
    runId: number | null;
    status: 'polling' | 'completed';
    results?: ExtractionResult[];
  } | null>(null);
  const [pendingExtractionAccepted, setPendingExtractionAccepted] = React.useState<
    ExtractionResult[]
  >([]);
  const [applyInProgress, setApplyInProgress] = React.useState(false);
  const [detailReloadSignal, setDetailReloadSignal] = React.useState(0);
  const [currentCustomerValues, setCurrentCustomerValues] = React.useState<
    Record<string, string | null>
  >({});

  // ── Create / edit form header-action state ─────────────────────────────────

  const handleCreateCancel = React.useCallback(() => {
    setFormState({ submitting: false, canSubmit: false, saveSucceeded: false });
    navigate(customersBasePath);
  }, [customersBasePath, navigate]);

  const handleStartEdit = React.useCallback(() => {
    if (!selectedCustomerUuid) return;
    navigate(`${customersBasePath}/${selectedCustomerUuid}/edit`);
    // Start disabled - CustomerForm's onFormStateChange will enable it once mounted
    setFormState({ submitting: false, canSubmit: false, saveSucceeded: false });
  }, [customersBasePath, navigate, selectedCustomerUuid]);

  const handleEndEdit = React.useCallback(() => {
    setFormState({ submitting: false, canSubmit: false, saveSucceeded: false });
    if (selectedCustomerUuid) {
      navigate(`${customersBasePath}/${selectedCustomerUuid}`);
    } else {
      navigate(customersBasePath);
    }
  }, [customersBasePath, navigate, selectedCustomerUuid]);

  // Called by CustomerDetailPage after a successful edit save.
  // Shows the check-icon state briefly, then resets - stays on the edit page.
  const handleEditSaveSucceeded = React.useCallback(() => {
    setFormState({ submitting: false, canSubmit: false, saveSucceeded: true });
    window.setTimeout(() => {
      setFormState({ submitting: false, canSubmit: false, saveSucceeded: false });
    }, 2000);
  }, []);

  const handleFormStateChange = React.useCallback(
    (state: { submitting: boolean; canSubmit: boolean }) => {
      setFormState((prev) => ({ ...state, saveSucceeded: prev.saveSucceeded }));
    },
    []
  );

  const handleBulkDelete = React.useCallback(async () => {
    setDeleteInProgress(true);
    try {
      await Promise.all(
        selectedCustomers.map((c) => customersApi.deleteCustomer(undefined, c.uuid))
      );
      setDeleteConfirmOpen(false);
      setSelectedCustomers([]);
      setRefreshKey((k) => k + 1);
    } finally {
      setDeleteInProgress(false);
    }
  }, [selectedCustomers]);

  const handleEditSelected = React.useCallback(() => {
    if (selectedCustomers.length !== 1) return;
    navigate(`${customersBasePath}/${selectedCustomers[0]!.uuid}/edit`);
    setFormState({ submitting: false, canSubmit: true, saveSucceeded: false });
  }, [customersBasePath, navigate, selectedCustomers]);

  const handleAttributeDesignerSave = React.useCallback(
    (draft: NewAttributeDraft) => {
      setPendingAttributeDraft(draft);
      // Optimistic update so the panel reflects immediately
      setRailState((cur) => ({
        ...cur,
        attributes: [
          ...cur.attributes.filter((a) => a.fieldKey !== normalizeFieldKey(draft.name)),
          {
            fieldKey: normalizeFieldKey(draft.name),
            name: draft.name,
            fieldType: draft.fieldType,
            sectionKey: draft.sectionKey,
            aiEnabled: draft.aiEnabled,
            description: draft.description || null,
            aiPrompt: draft.aiPrompt || null,
            scope: 'user' as const,
          },
        ],
      }));
      // Persist to customer_template_field immediately (don't wait for customer creation)
      void (async () => {
        try {
          const { template } = await customersApi.updateTemplate(undefined, {
            attributes: [
              {
                name: draft.name,
                fieldType: draft.fieldType,
                sectionKey: draft.sectionKey,
                aiEnabled: draft.aiEnabled,
                description: draft.description || null,
                aiPrompt: draft.aiPrompt || null,
                scope: 'user',
              },
            ],
          });
          setRailState((cur) => ({ ...cur, template }));
        } catch {
          // ignore
        }
      })();
      setAddingAttribute(false);
      setRightPaneVisible(true);
      setRightPaneMode('attribute-descriptions');
    },
    [setRightPaneMode, setRightPaneVisible]
  );

  const {
    confirmIfDirty,
    register: registerDirty,
    unregister: unregisterDirty,
  } = useDirtyStateContext();

  const handleAttributeDesignerCancel = React.useCallback(async () => {
    if (!(await confirmIfDirty('rr:attribute-designer'))) return;
    setAddingAttribute(false);
    setEditingAttribute(null);
    setRightPaneVisible(true);
    setRightPaneMode('attribute-descriptions');
  }, [confirmIfDirty, setRightPaneMode, setRightPaneVisible]);

  const handleAddAttributeRequest = React.useCallback(() => {
    setEditingAttribute(null);
    setAddingAttribute(true);
    setRightPaneVisible(true);
    setRightPaneMode('attribute-descriptions');
  }, [setRightPaneMode, setRightPaneVisible]);

  const handleEditAttributeRequest = React.useCallback(
    (attribute: CustomerRailAttribute) => {
      setAddingAttribute(false);
      setEditingAttribute(attribute);
      setRightPaneVisible(true);
      setRightPaneMode('attribute-descriptions');
    },
    [setRightPaneMode, setRightPaneVisible]
  );

  const handleEditAttributeSave = React.useCallback(
    async (draft: NewAttributeDraft) => {
      if (!editingAttribute) return;
      const fieldKey = editingAttribute.fieldKey;
      // Optimistic update
      setRailState((cur) => {
        if (!cur.template) return cur;
        const updatedUserAttrs = cur.template.userAttributes.map((attr) =>
          attr.fieldKey === fieldKey
            ? {
                ...attr,
                name: draft.name,
                description: draft.description || null,
                aiPrompt: draft.aiPrompt || null,
                aiEnabled: draft.aiEnabled,
              }
            : attr
        );
        const updatedAttributes = cur.attributes.map((attr) =>
          attr.fieldKey === fieldKey
            ? {
                ...attr,
                name: draft.name,
                description: draft.description || null,
                aiPrompt: draft.aiPrompt || null,
                aiEnabled: draft.aiEnabled,
              }
            : attr
        );
        return {
          ...cur,
          attributes: updatedAttributes,
          template: {
            ...cur.template,
            userAttributes: updatedUserAttrs,
            mergedAttributes: [...cur.template.workspaceAttributes, ...updatedUserAttrs],
          },
        };
      });
      setEditingAttribute(null);
      void (async () => {
        try {
          const { template } = await customersApi.updateTemplate(undefined, {
            attributes: [
              {
                fieldKey,
                name: draft.name,
                fieldType: editingAttribute.fieldType,
                sectionKey: editingAttribute.sectionKey,
                aiEnabled: draft.aiEnabled,
                description: draft.description || null,
                aiPrompt: draft.aiPrompt || null,
                scope: 'user',
              },
            ],
          });
          setRailState((cur) => ({ ...cur, template }));
        } catch {
          // ignore
        }
      })();
    },
    [editingAttribute]
  );

  const handleEditAttributeCancel = React.useCallback(async () => {
    if (!(await confirmIfDirty('rr:attribute-designer'))) return;
    setEditingAttribute(null);
    setRightPaneVisible(true);
    setRightPaneMode('attribute-descriptions');
  }, [confirmIfDirty, setRightPaneMode, setRightPaneVisible]);

  const handleEditAttributeDelete = React.useCallback(() => {
    if (!editingAttribute) return;
    const fieldKey = editingAttribute.fieldKey;
    const name = editingAttribute.name;
    setDeletedAttributeSignal({ fieldKey, name, nonce: Date.now() });
    // Optimistic removal from rail state
    setRailState((cur) => {
      if (!cur.template) return cur;
      const updatedUserAttrs = cur.template.userAttributes.filter(
        (attr) => attr.fieldKey !== fieldKey
      );
      return {
        ...cur,
        template: {
          ...cur.template,
          userAttributes: updatedUserAttrs,
          mergedAttributes: [...cur.template.workspaceAttributes, ...updatedUserAttrs],
        },
        attributes: cur.attributes.filter((attr) => attr.fieldKey !== fieldKey),
      };
    });
    setEditingAttribute(null);
    void (async () => {
      try {
        await customersApi.deleteTemplateAttribute(fieldKey);
        // Re-fetch template to ensure consistency
        const { template } = await customersApi.getTemplate();
        setRailState((cur) => ({ ...cur, template }));
      } catch {
        // On error re-fetch to restore actual state
        try {
          const { template } = await customersApi.getTemplate();
          setRailState((cur) => ({ ...cur, template }));
        } catch {
          // ignore
        }
      }
    })();
  }, [editingAttribute]);

  const handlePendingAttributeConsumed = React.useCallback(() => {
    setPendingAttributeDraft(null);
  }, []);

  // ── Document add handlers ──────────────────────────────────────────────────

  const handleDocumentAddSave = React.useCallback(
    async (draft: DocumentAddDraft) => {
      if (!selectedCustomerUuid) return;
      let created: CustomerDocument;
      if (draft.kind === 'paste') {
        const res = await customersApi.createPasteDocument(
          selectedCustomerUuid,
          draft.text,
          draft.title || undefined
        );
        created = res.document;
      } else {
        const res = await customersApi.createUploadDocument(
          selectedCustomerUuid,
          draft.file,
          draft.title || undefined
        );
        created = res.document;
      }
      // Seed the document list with the pending doc before the panel closes
      // so the processing indicator is visible during the brief pending window.
      setLastAddedDocument(created);
      setDocumentAddMode(null);
      setCanSubmitDocumentAdd(false);
    },
    [selectedCustomerUuid]
  );

  const handleDocumentAddCancel = React.useCallback(async () => {
    if (!(await confirmIfDirty('rr:document-add'))) return;
    // If we were editing, return to the view panel
    if (editingDocumentUuid) {
      setViewingDocumentUuid(editingDocumentUuid);
      setEditingDocumentUuid(null);
    }
    setDocumentAddMode(null);
    setCanSubmitDocumentAdd(false);
    setDocumentSaveState('idle');
  }, [confirmIfDirty, editingDocumentUuid]);

  const handleViewDocument = React.useCallback(
    (
      docUuid: string,
      sourceKind: DocumentSourceKind,
      chunkStatus: 'pending' | 'chunked' | 'failed' | null
    ) => {
      setViewingDocumentUuid(docUuid);
      setViewingDocumentKind(sourceKind);
      setViewingDocumentChunkStatus(chunkStatus);
    },
    []
  );

  const handleDocumentViewBack = React.useCallback(() => {
    setViewingDocumentUuid(null);
    setViewingDocumentKind(null);
    setViewingDocumentChunkStatus(null);
  }, []);

  const handleEditDocument = React.useCallback((docUuid: string) => {
    setEditingDocumentUuid(docUuid);
    setViewingDocumentUuid(null);
    setViewingDocumentChunkStatus(null);
    setDocumentAddMode('paste');
    setCanSubmitDocumentAdd(false);
    setDocumentSaveState('idle');
  }, []);

  // ── Extraction handlers ────────────────────────────────────────────────────

  const handleExtractRequest = React.useCallback(
    async (docUuid: string) => {
      if (!selectedCustomerUuid) return;
      // Set polling state immediately so the UI switches without waiting for the network
      setExtractionPanel({ docUuid, runId: null, status: 'polling' });
      const { runId } = await customersApi.startExtraction(selectedCustomerUuid, docUuid);
      setExtractionPanel((prev) => (prev ? { ...prev, runId } : null));
      // Snapshot existing attribute values for the comparison UI (fire-and-forget alongside polling)
      void customersApi
        .getCustomer(undefined, selectedCustomerUuid)
        .then(({ customer: cust }) => {
          const vals: Record<string, string | null> = {};
          for (const attr of cust.attributes) {
            if (attr.value === null || attr.value === undefined) {
              vals[attr.fieldKey] = null;
            } else if (Array.isArray(attr.value)) {
              vals[attr.fieldKey] = (attr.value as unknown[]).join(', ');
            } else {
              vals[attr.fieldKey] = String(attr.value);
            }
          }
          setCurrentCustomerValues(vals);
        })
        .catch(() => {
          setCurrentCustomerValues({});
        });
      // Poll every 2 s until completed or failed
      const poll = () => {
        void customersApi
          .pollExtractionRun(runId as number)
          .then((result) => {
            if (result.status === 'completed') {
              setExtractionPanel((prev) =>
                prev ? { ...prev, status: 'completed', results: result.extractions ?? [] } : null
              );
            } else if (result.status === 'failed') {
              setExtractionPanel(null);
            } else {
              window.setTimeout(poll, 2000);
            }
          })
          .catch(() => {
            window.setTimeout(poll, 2000);
          });
      };
      window.setTimeout(poll, 2000);
    },
    [selectedCustomerUuid]
  );

  const handleApplyExtractions = React.useCallback(
    async (accepted: ExtractionResult[]) => {
      if (!selectedCustomerUuid || !extractionPanel) return;
      setApplyInProgress(true);
      try {
        await customersApi.applyExtractions(selectedCustomerUuid, {
          extractions: accepted.map((e) => ({ ...e, docUuid: extractionPanel.docUuid })),
        });
        await customersApi.pollApplyRun(0);
        setExtractionPanel(null);
        setPendingExtractionAccepted([]);
        setDetailReloadSignal((k) => k + 1);
        setRefreshKey((k) => k + 1);
      } finally {
        setApplyInProgress(false);
      }
    },
    [selectedCustomerUuid, extractionPanel]
  );

  React.useEffect(() => {
    if (!addingAttribute) {
      setCanSubmitAttributeCreate(false);
    }
  }, [addingAttribute]);

  // ── Import CSV handlers ────────────────────────────────────────────────────

  const handleCsvFileChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset so the same file can be re-selected
    (e.target as HTMLInputElement).value = '';
    if (!file) return;

    // Upload to preview endpoint → AI infers mapping → open rail
    savePaneWidth();
    setCsvImportFile(file);
    setCsvPreviewData(null);
    setImportCsvOpen(true);

    void (async () => {
      try {
        const { runId } = await customersApi.startCsvPreview(file);
        const poll = async () => {
          try {
            const res = await customersApi.pollCsvPreviewRun(runId);
            if (res.status === 'completed' && res.preview) {
              setCsvPreviewData({
                ...res.preview,
                newRows: res.preview.newRows ?? res.preview.totalRows,
                duplicateRows: res.preview.duplicateRows ?? 0,
                noContactRows: res.preview.noContactRows ?? 0,
                rawMappings: Array.isArray(res.preview.rawMappings) ? res.preview.rawMappings : [],
                mappings: Array.isArray(res.preview.mappings) ? res.preview.mappings : [],
                preview: Array.isArray(res.preview.preview) ? res.preview.preview : [],
              });
            } else if (res.status === 'failed') {
              // Keep rail open but with empty preview so user can still close
              setCsvPreviewData({
                filename: file.name,
                totalRows: 0,
                newRows: 0,
                duplicateRows: 0,
                noContactRows: 0,
                rawMappings: [],
                mappedColumns: 0,
                mappings: [],
                preview: [],
              });
            } else {
              window.setTimeout(() => void poll(), 2000);
            }
          } catch {
            window.setTimeout(() => void poll(), 2000);
          }
        };
        window.setTimeout(() => void poll(), 2000);
      } catch {
        setCsvPreviewData({
          filename: file.name,
          totalRows: 0,
          newRows: 0,
          duplicateRows: 0,
          noContactRows: 0,
          rawMappings: [],
          mappedColumns: 0,
          mappings: [],
          preview: [],
        });
      }
    })();
  }, []);

  const handleCsvImportAdd = React.useCallback(async () => {
    if (!csvImportFile) return;
    setCsvImportInProgress(true);
    try {
      const { runId } = await customersApi.startImportCsv(
        csvImportFile,
        csvPreviewData?.rawMappings
      );

      // Poll until the background import job finishes
      while (true) {
        const res = await customersApi.pollCsvImportRun(runId);
        if (res.status === 'completed' || res.status === 'failed') {
          break;
        }
        await new Promise((resolve) => window.setTimeout(resolve, 2000));
      }

      setRefreshKey((k) => k + 1);
    } catch (err) {
      console.error('CSV import failed:', err);
    } finally {
      setCsvImportInProgress(false);
      setImportCsvOpen(false);
      setCsvImportFile(null);
      setCsvPreviewData(null);
      restorePaneWidth();
    }
  }, [csvImportFile, csvPreviewData, restorePaneWidth]);

  const handleCsvImportClose = React.useCallback(() => {
    setImportCsvOpen(false);
    setCsvImportFile(null);
    setCsvPreviewData(null);
    setCsvImportInProgress(false);
    unregisterDirty('csv-import');
    restorePaneWidth();
  }, [unregisterDirty, restorePaneWidth]);

  // Register dirty state while a CSV file is loaded so the DirtyStateContext
  // blocks page refresh and in-app navigation automatically.
  React.useEffect(() => {
    if (csvImportFile) {
      registerDirty('csv-import', true, 'CSV import');
    } else {
      unregisterDirty('csv-import');
    }
  }, [csvImportFile, registerDirty, unregisterDirty]);

  const handleCsvCancelClick = React.useCallback(() => {
    void confirmIfDirty('csv-import').then((ok) => {
      if (ok) handleCsvImportClose();
    });
  }, [confirmIfDirty, handleCsvImportClose]);

  // ── Import Archon handlers ─────────────────────────────────────────────────

  const handleImportAnalyze = React.useCallback(async (text: string) => {
    setImportPanel({ status: 'polling', runId: null });
    const { runId } = await customersApi.startImportArchon(text);
    setImportPanel((prev) => (prev ? { ...prev, runId } : null));

    const poll = () => {
      void customersApi
        .pollExtractionRun(runId)
        .then((result) => {
          if (result.status === 'completed') {
            const extractions = result.extractions ?? [];
            setImportPanel({ status: 'review', runId, results: extractions });
            // Check for duplicate email or linkedin after analysis completes
            const emailValue = extractions.find((r) => r.fieldKey === 'email')?.value ?? null;
            const linkedinValue = extractions.find((r) => r.fieldKey === 'linkedin')?.value ?? null;
            setImportDuplicate(null);
            if (emailValue) {
              void customersApi.checkEmailExists(emailValue).then((res) => {
                if (res.exists) {
                  setImportDuplicate({
                    matchedBy: 'email',
                    customerName: res.customerName,
                    customerUuid: res.customerUuid,
                  });
                } else if (linkedinValue) {
                  void customersApi.checkLinkedinExists(linkedinValue).then((lres) => {
                    if (lres.exists) {
                      setImportDuplicate({
                        matchedBy: 'linkedin',
                        customerName: lres.customerName,
                        customerUuid: lres.customerUuid,
                      });
                    }
                  });
                }
              });
            } else if (linkedinValue) {
              void customersApi.checkLinkedinExists(linkedinValue).then((lres) => {
                if (lres.exists) {
                  setImportDuplicate({
                    matchedBy: 'linkedin',
                    customerName: lres.customerName,
                    customerUuid: lres.customerUuid,
                  });
                }
              });
            }
          } else if (result.status === 'failed') {
            setImportPanel(null);
          } else {
            window.setTimeout(poll, 2000);
          }
        })
        .catch(() => {
          window.setTimeout(poll, 2000);
        });
    };
    window.setTimeout(poll, 2000);
  }, []);

  const handleImportCreate = React.useCallback(async () => {
    if (importAccepted.length === 0) return;
    setImportCreateInProgress(true);
    try {
      const IDENTITY_KEYS = new Set(['name', 'email', 'phone']);
      const nameResult = importAccepted.find((r) => r.fieldKey === 'name');
      const emailResult = importAccepted.find((r) => r.fieldKey === 'email');
      const phoneResult = importAccepted.find((r) => r.fieldKey === 'phone');
      const attrResults = importAccepted.filter((r) => !IDENTITY_KEYS.has(r.fieldKey));

      // Step 1: create customer with sourceType 'ai'
      const { customer } = await customersApi.createCustomer(undefined, {
        name: nameResult?.value ?? 'Unnamed',
        email: emailResult?.value ?? null,
        phone: phoneResult?.value ?? null,
        sourceType: 'ai',
      });

      // Step 2: create the source document from the import text
      let docUuid = '';
      if (importText.trim()) {
        const { document } = await customersApi.createPasteDocument(
          customer.uuid,
          importText.trim(),
          importTitle.trim() || undefined
        );
        docUuid = document.uuid;
      }

      // Step 3: apply non-identity extractions as template field values
      if (attrResults.length > 0) {
        await customersApi.applyExtractions(customer.uuid, {
          extractions: attrResults.filter((r) => r.value !== null).map((r) => ({ ...r, docUuid })),
        });
      }

      setImportCreatedUuid(customer.uuid);
      setImportPanel(null);
      setRefreshKey((k) => k + 1);
    } finally {
      setImportCreateInProgress(false);
    }
  }, [importAccepted, importText, importTitle]);

  const handleImportAnother = React.useCallback(() => {
    setImportCreatedUuid(null);
    setImportPanel(null);
    setImportAccepted([]);
    setImportDuplicate(null);
    setImportText('');
    setImportTitle('');
  }, []);

  const handleImportClose = React.useCallback(() => {
    setImportArchonOpen(false);
    setImportPanel(null);
    setImportAccepted([]);
    setImportCreatedUuid(null);
    setImportDuplicate(null);
    setImportCreateInProgress(false);
    setImportText('');
    setImportTitle('');
    unregisterDirty('archon-import');
    restorePaneWidth();
  }, [unregisterDirty, restorePaneWidth]);

  // Register dirty state while archon import has content so DirtyStateContext
  // handles beforeunload and in-app navigation automatically.
  // Once importCreatedUuid is set the customer was successfully saved - no longer dirty.
  React.useEffect(() => {
    if (!importCreatedUuid && (importText.trim() || importPanel !== null)) {
      registerDirty('archon-import', true, 'Document import');
    } else {
      unregisterDirty('archon-import');
    }
  }, [importText, importPanel, importCreatedUuid, registerDirty, unregisterDirty]);

  const handleImportCancelClick = React.useCallback(() => {
    void confirmIfDirty('archon-import').then((ok) => {
      if (ok) handleImportClose();
    });
  }, [confirmIfDirty, handleImportClose]);

  // Duplicate warning is only active while the user still has the triggering field checked.
  // If they uncheck email/linkedin, clear the block so Create is re-enabled.
  const activeDuplicate = React.useMemo(() => {
    if (!importDuplicate) return null;
    const fieldStillAccepted = importAccepted.some((r) => r.fieldKey === importDuplicate.matchedBy);
    return fieldStillAccepted ? importDuplicate : null;
  }, [importDuplicate, importAccepted]);

  const rightRailItems: RightRailItem[] | undefined = React.useMemo(() => {
    // Import CSV pane - standalone, shown on the list view when CSV import is open
    if (importCsvOpen && !(creatingCustomer || detailEditing || detailViewing)) {
      return [
        {
          id: 'import-csv',
          label: 'Import from CSV',
          icon: (
            <ArchonIcon size={IconSize.sm} isRunning={!csvPreviewData || csvImportInProgress} />
          ),
          noPadding: true,
          headerAction: !csvPreviewData ? (
            <>
              <Tooltip.Root>
                <Tooltip.Trigger asChild>
                  <span style={{ display: 'inline-flex', cursor: 'not-allowed' }}>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled
                      style={{ pointerEvents: 'none' }}
                    >
                      Cancel
                    </Button>
                  </span>
                </Tooltip.Trigger>
                <Tooltip.Portal>
                  <Tooltip.Content side="bottom" align="end" sideOffset={4} className="ui-tooltip">
                    Analysis in progress
                    <Tooltip.Arrow className="ui-tooltip-arrow" />
                  </Tooltip.Content>
                </Tooltip.Portal>
              </Tooltip.Root>
              <Button type="button" variant="primary" size="sm" disabled>
                Analyzing…
              </Button>
            </>
          ) : (
            <>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={csvImportInProgress}
                onClick={handleCsvCancelClick}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                size="sm"
                disabled={csvImportInProgress || !csvImportFile || csvPreviewData.newRows === 0}
                onClick={() => void handleCsvImportAdd()}
              >
                {csvImportInProgress ? 'Adding…' : `Add (${csvPreviewData.newRows})`}
              </Button>
            </>
          ),
          content: <CustomerImportCsvPanel preview={csvPreviewData} />,
        },
      ];
    }

    // Import Archon pane - standalone, shown on the list view when import is open
    if (importArchonOpen && !(creatingCustomer || detailEditing || detailViewing)) {
      return [
        {
          id: 'import-archon',
          label: 'Import via document',
          icon: <ArchonIcon size={IconSize.sm} isRunning={importPanel?.status === 'polling'} />,
          noPadding: true,
          headerAction: importCreatedUuid ? (
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={() => {
                unregisterDirty('archon-import');
                navigate(`${customersBasePath}/${importCreatedUuid}`);
                handleImportClose();
              }}
            >
              View
            </Button>
          ) : importPanel?.status === 'review' ? (
            <>
              {importCreateInProgress ? (
                <Tooltip.Root>
                  <Tooltip.Trigger asChild>
                    <span style={{ display: 'inline-flex', cursor: 'not-allowed' }}>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled
                        style={{ pointerEvents: 'none' }}
                      >
                        Cancel
                      </Button>
                    </span>
                  </Tooltip.Trigger>
                  <Tooltip.Portal>
                    <Tooltip.Content
                      side="bottom"
                      align="end"
                      sideOffset={4}
                      className="ui-tooltip"
                    >
                      Creation in progress
                      <Tooltip.Arrow className="ui-tooltip-arrow" />
                    </Tooltip.Content>
                  </Tooltip.Portal>
                </Tooltip.Root>
              ) : (
                <Button type="button" variant="ghost" size="sm" onClick={handleImportCancelClick}>
                  Cancel
                </Button>
              )}
              {importCreateInProgress ? (
                <Tooltip.Root>
                  <Tooltip.Trigger asChild>
                    <span style={{ display: 'inline-flex', cursor: 'not-allowed' }}>
                      <Button
                        type="button"
                        variant="primary"
                        size="sm"
                        disabled
                        style={{ pointerEvents: 'none' }}
                      >
                        Creating…
                      </Button>
                    </span>
                  </Tooltip.Trigger>
                  <Tooltip.Portal>
                    <Tooltip.Content
                      side="bottom"
                      align="end"
                      sideOffset={4}
                      className="ui-tooltip"
                    >
                      Creation in progress
                      <Tooltip.Arrow className="ui-tooltip-arrow" />
                    </Tooltip.Content>
                  </Tooltip.Portal>
                </Tooltip.Root>
              ) : (
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  disabled={importAccepted.length === 0 || activeDuplicate !== null}
                  onClick={() => void handleImportCreate()}
                >
                  {`Create (${importAccepted.length})`}
                </Button>
              )}
            </>
          ) : importPanel?.status === 'polling' ? (
            <>
              <Tooltip.Root>
                <Tooltip.Trigger asChild>
                  <span style={{ display: 'inline-flex', cursor: 'not-allowed' }}>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled
                      style={{ pointerEvents: 'none' }}
                    >
                      Cancel
                    </Button>
                  </span>
                </Tooltip.Trigger>
                <Tooltip.Portal>
                  <Tooltip.Content side="bottom" align="end" sideOffset={4} className="ui-tooltip">
                    Analysis in progress
                    <Tooltip.Arrow className="ui-tooltip-arrow" />
                  </Tooltip.Content>
                </Tooltip.Portal>
              </Tooltip.Root>
              <Tooltip.Root>
                <Tooltip.Trigger asChild>
                  <span style={{ display: 'inline-flex', cursor: 'not-allowed' }}>
                    <Button
                      type="button"
                      variant="primary"
                      size="sm"
                      disabled
                      style={{ pointerEvents: 'none' }}
                    >
                      Analyzing…
                    </Button>
                  </span>
                </Tooltip.Trigger>
                <Tooltip.Portal>
                  <Tooltip.Content side="bottom" align="end" sideOffset={4} className="ui-tooltip">
                    Analysis in progress
                    <Tooltip.Arrow className="ui-tooltip-arrow" />
                  </Tooltip.Content>
                </Tooltip.Portal>
              </Tooltip.Root>
            </>
          ) : (
            <>
              <Button type="button" variant="ghost" size="sm" onClick={handleImportCancelClick}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                size="sm"
                disabled={!importText.trim()}
                onClick={() => void handleImportAnalyze(importText.trim())}
              >
                Import
              </Button>
            </>
          ),
          content: (
            <CustomerImportArchonPanel
              importPanel={importPanel}
              attributeDefs={railState.template?.mergedAttributes ?? []}
              sections={railState.template?.sections ?? undefined}
              onTextChange={setImportText}
              onTitleChange={setImportTitle}
              onSelectionChange={setImportAccepted}
              createInProgress={importCreateInProgress}
              createdCustomerUuid={importCreatedUuid}
              onImportAnother={handleImportAnother}
              duplicate={activeDuplicate}
            />
          ),
        },
      ];
    }

    if (!(creatingCustomer || detailEditing || detailViewing)) return undefined;

    const readOnlyViewMode = detailViewing;
    const railHeaderLabel = addingAttribute
      ? 'Create attribute'
      : editingAttribute
        ? 'Edit attribute'
        : 'Attribute descriptions';

    let headerAction: React.ReactNode = null;
    // Capture extractionPanel in a local const so TypeScript's control-flow
    // narrowing inside ternary branches doesn't widen it to `never`.
    const _capturedExtractionPanel = extractionPanel;
    if (readOnlyViewMode) {
      headerAction = null;
    } else if (addingAttribute) {
      headerAction = (
        <>
          <Button
            key="cancel-add"
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleAttributeDesignerCancel}
          >
            Cancel
          </Button>
          <Button
            key="submit-add"
            type="submit"
            form="customer-attribute-designer-form"
            variant="primary"
            size="sm"
            disabled={!canSubmitAttributeCreate}
          >
            Add attribute
          </Button>
        </>
      );
    } else if (editingAttribute) {
      headerAction = (
        <>
          <Button
            key="cancel-edit"
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleEditAttributeCancel}
          >
            Cancel
          </Button>
          <Button
            key="submit-edit"
            type="submit"
            form="customer-attribute-designer-form"
            variant="primary"
            size="sm"
          >
            Save changes
          </Button>
        </>
      );
    } else if (isWorkspaceAdmin) {
      headerAction = (
        <Button
          key="create"
          type="button"
          variant="primary"
          size="sm"
          onClick={handleAddAttributeRequest}
        >
          Create attribute
        </Button>
      );
    }

    let content: React.ReactNode;
    if (readOnlyViewMode) {
      content = <AttributeDescriptionsPanel state={railState} />;
    } else if (addingAttribute) {
      const existingAttrNames = [
        ...(railState.template?.workspaceAttributes ?? []).map((a) => a.name),
        ...(railState.template?.userAttributes ?? []).map((a) => a.name),
        ...railState.attributes.map((a) => a.name),
      ];
      content = (
        <AttributeDesignerPanel
          formId="customer-attribute-designer-form"
          hideActions
          mode="create"
          loading={!railState.template}
          existingNames={existingAttrNames}
          sections={railState.template?.sections ?? undefined}
          onValidityChange={setCanSubmitAttributeCreate}
          onSave={handleAttributeDesignerSave}
          onCancel={handleAttributeDesignerCancel}
        />
      );
    } else if (editingAttribute) {
      const editExistingNames = [
        ...(railState.template?.workspaceAttributes ?? []).map((a) => a.name),
        ...(railState.template?.userAttributes ?? []).map((a) => a.name),
        ...railState.attributes.map((a) => a.name),
      ].filter((n) => n.toLowerCase() !== editingAttribute.name.toLowerCase());
      content = (
        <AttributeDesignerPanel
          formId="customer-attribute-designer-form"
          hideActions
          mode="edit"
          existingNames={editExistingNames}
          sections={railState.template?.sections ?? undefined}
          initial={{
            name: editingAttribute.name,
            fieldType: editingAttribute.fieldType,
            sectionKey: editingAttribute.sectionKey,
            aiEnabled: editingAttribute.aiEnabled,
            description: editingAttribute.description ?? '',
            aiPrompt: editingAttribute.aiPrompt ?? '',
          }}
          onSave={(draft) => void handleEditAttributeSave(draft)}
          onCancel={handleEditAttributeCancel}
          onDelete={handleEditAttributeDelete}
        />
      );
    } else {
      content = (
        <AttributeDescriptionsPanel
          state={railState}
          onEditAttribute={isWorkspaceAdmin ? handleEditAttributeRequest : undefined}
          sectionFilter={creatingCustomer || detailEditing ? 'identity' : undefined}
        />
      );
    }

    return [
      {
        id: 'attribute-descriptions',
        label: railHeaderLabel,
        icon: <ListBulletIcon width={16} height={16} aria-hidden="true" />,
        headerAction,
        content,
      },
      ...(detailEditing || detailViewing
        ? [
            {
              id: 'customer-documents',
              noPadding: !!viewingDocumentUuid || !!documentAddMode,
              label:
                documentAddMode === 'paste'
                  ? 'Add document'
                  : documentAddMode === 'upload'
                    ? 'Upload file'
                    : viewingDocumentUuid
                      ? _capturedExtractionPanel
                        ? 'Extraction review'
                        : 'View document'
                      : 'Documents',
              icon: <FileTextIcon width={16} height={16} aria-hidden="true" />,
              headerAction: viewingDocumentUuid ? (
                <>
                  {(() => {
                    const isViewingDocumentProcessing = viewingDocumentChunkStatus === 'pending';
                    const editDisabled =
                      viewingDocumentKind === 'upload' || isViewingDocumentProcessing;
                    const editDisabledMessage = isViewingDocumentProcessing
                      ? 'Document is being processed'
                      : 'Uploaded files cannot be edited';

                    // Extraction active - show Cancel + Apply
                    if (_capturedExtractionPanel) {
                      return (
                        <>
                          <Button
                            key="cancel-extraction"
                            type="button"
                            variant="ghost"
                            size="sm"
                            disabled={
                              _capturedExtractionPanel.status === 'polling' || applyInProgress
                            }
                            onClick={() => setCancelExtractionConfirmOpen(true)}
                          >
                            Cancel
                          </Button>
                          <Button
                            key="apply-extraction"
                            type="button"
                            variant="primary"
                            size="sm"
                            disabled={pendingExtractionAccepted.length === 0 || applyInProgress}
                            onClick={() => void handleApplyExtractions(pendingExtractionAccepted)}
                          >
                            {applyInProgress
                              ? 'Applying…'
                              : `Apply (${pendingExtractionAccepted.length})`}
                          </Button>
                        </>
                      );
                    }

                    // Normal view mode - Back + Edit
                    return (
                      <>
                        <Button
                          key="back-doc-view"
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={handleDocumentViewBack}
                        >
                          Back
                        </Button>
                        <Tooltip.Root>
                          <Tooltip.Trigger asChild>
                            <span
                              style={{
                                display: 'inline-flex',
                                cursor: editDisabled ? 'not-allowed' : undefined,
                              }}
                            >
                              <Button
                                key="edit-doc-view"
                                type="button"
                                variant="primary"
                                size="sm"
                                disabled={editDisabled}
                                style={editDisabled ? { pointerEvents: 'none' } : undefined}
                                onClick={() => handleEditDocument(viewingDocumentUuid)}
                              >
                                Edit
                              </Button>
                            </span>
                          </Tooltip.Trigger>
                          {editDisabled && (
                            <Tooltip.Portal>
                              <Tooltip.Content
                                side="bottom"
                                align="end"
                                sideOffset={4}
                                className="ui-tooltip"
                              >
                                {editDisabledMessage}
                                <Tooltip.Arrow className="ui-tooltip-arrow" />
                              </Tooltip.Content>
                            </Tooltip.Portal>
                          )}
                        </Tooltip.Root>
                      </>
                    );
                  })()}
                </>
              ) : documentAddMode ? (
                <>
                  <Button
                    key="cancel-doc-add"
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleDocumentAddCancel}
                  >
                    {editingDocumentUuid ? 'Back' : 'Cancel'}
                  </Button>
                  <Button
                    key="save-doc-add"
                    type="submit"
                    form="customer-document-add-form"
                    variant="primary"
                    size="sm"
                    disabled={!canSubmitDocumentAdd || documentSaveState !== 'idle'}
                  >
                    {documentSaveState === 'saving' ? (
                      <ReloadIcon
                        width={14}
                        height={14}
                        className="customer-notes__save-spinner"
                        aria-label="Saving"
                      />
                    ) : documentSaveState === 'saved' ? (
                      <CheckIcon width={14} height={14} aria-hidden="true" />
                    ) : (
                      'Save'
                    )}
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    key="upload-doc"
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setDocumentAddMode('upload')}
                  >
                    Upload
                  </Button>
                  <Button
                    key="add-doc"
                    type="button"
                    variant="primary"
                    size="sm"
                    onClick={() => setDocumentAddMode('paste')}
                  >
                    Add
                  </Button>
                </>
              ),
              content: viewingDocumentUuid ? (
                <CustomerDocumentViewPanel
                  customerUuid={selectedCustomerUuid!}
                  docUuid={viewingDocumentUuid}
                  sourceKind={viewingDocumentKind!}
                  onChunkStatusChange={setViewingDocumentChunkStatus}
                  onExtractRequest={() => void handleExtractRequest(viewingDocumentUuid)}
                  extractionResults={
                    _capturedExtractionPanel
                      ? _capturedExtractionPanel.status === 'polling'
                        ? null
                        : (_capturedExtractionPanel.results ?? [])
                      : undefined
                  }
                  existingValues={_capturedExtractionPanel ? currentCustomerValues : undefined}
                  onApplyExtraction={(accepted) => void handleApplyExtractions(accepted)}
                  onCancelExtraction={() => {
                    setExtractionPanel(null);
                    setPendingExtractionAccepted([]);
                  }}
                  onExtractionSelectionChange={setPendingExtractionAccepted}
                  attributeDefs={railState.template?.mergedAttributes ?? []}
                  sections={railState.template?.sections ?? undefined}
                  applyInProgress={applyInProgress}
                />
              ) : documentAddMode ? (
                <CustomerDocumentAddPanel
                  mode={documentAddMode}
                  formId="customer-document-add-form"
                  hideActions
                  onSave={handleDocumentAddSave}
                  onSaveStateChange={setDocumentSaveState}
                  onCancel={handleDocumentAddCancel}
                  onValidityChange={setCanSubmitDocumentAdd}
                  docUuid={editingDocumentUuid ?? undefined}
                  customerUuid={selectedCustomerUuid ?? undefined}
                  onEditSaved={(doc) => setViewingDocumentChunkStatus(doc.chunkStatus)}
                />
              ) : (
                <CustomerDocumentsPanel
                  customerUuid={selectedCustomerUuid!}
                  onViewDocument={handleViewDocument}
                  optimisticDocument={lastAddedDocument}
                  onOptimisticDocumentConsumed={() => setLastAddedDocument(null)}
                />
              ),
            },
          ]
        : []),
    ];
  }, [
    addingAttribute,
    editingAttribute,
    creatingCustomer,
    detailEditing,
    detailViewing,
    isWorkspaceAdmin,
    railState,
    handleAddAttributeRequest,
    handleAttributeDesignerCancel,
    handleAttributeDesignerSave,
    canSubmitAttributeCreate,
    handleEditAttributeCancel,
    handleEditAttributeDelete,
    handleEditAttributeRequest,
    handleEditAttributeSave,
    selectedCustomerUuid,
    documentAddMode,
    canSubmitDocumentAdd,
    documentSaveState,
    setDocumentSaveState,
    extractionPanel,
    pendingExtractionAccepted,
    applyInProgress,
    currentCustomerValues,
    handleDocumentAddSave,
    handleDocumentAddCancel,
    viewingDocumentUuid,
    viewingDocumentKind,
    viewingDocumentChunkStatus,
    editingDocumentUuid,
    handleViewDocument,
    handleDocumentViewBack,
    handleEditDocument,
    handleExtractRequest,
    handleApplyExtractions,
    importArchonOpen,
    importPanel,
    importAccepted,
    importCreateInProgress,
    importCreatedUuid,
    importText,
    handleImportAnalyze,
    handleImportCreate,
    handleImportClose,
    setImportAccepted,
    importCsvOpen,
    csvImportFile,
    csvPreviewData,
    csvImportInProgress,
    handleCsvImportAdd,
    handleCsvImportClose,
    navigate,
    customersBasePath,
  ]);

  let headerActions: React.ReactNode = null;
  if (creatingCustomer) {
    headerActions = (
      <>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleCreateCancel}
          disabled={formState.submitting}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          form="customer-create-form"
          variant="primary"
          size="sm"
          disabled={!formState.canSubmit}
        >
          Create
        </Button>
      </>
    );
  } else if (selectedCustomerUuid && detailEditing) {
    headerActions = (
      <>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleEndEdit}
          disabled={formState.submitting || formState.saveSucceeded}
        >
          Back
        </Button>
        <Button
          type="submit"
          form="customer-edit-form"
          variant="primary"
          size="sm"
          disabled={!formState.canSubmit || formState.saveSucceeded}
        >
          {formState.submitting ? (
            <ReloadIcon
              width={14}
              height={14}
              className="customer-notes__save-spinner"
              aria-label="Saving"
            />
          ) : formState.saveSucceeded ? (
            <CheckIcon width={14} height={14} aria-hidden="true" />
          ) : (
            'Update customer'
          )}
        </Button>
      </>
    );
  } else if (selectedCustomerUuid && !detailEditing) {
    headerActions = (
      <Button type="button" variant="outline" size="sm" onClick={() => navigate(customersBasePath)}>
        Back to list
      </Button>
    );
  } else if (
    !selectedCustomerUuid &&
    !creatingCustomer &&
    selectedCustomers.length > 0 &&
    isWorkspaceAdmin
  ) {
    headerActions = (
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <Button type="button" variant="outline" size="sm">
            {selectedCustomers.length} selected
            <ChevronDownIcon width={12} height={12} aria-hidden="true" />
          </Button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            className="dropdown-content"
            align="end"
            sideOffset={6}
            style={{ minWidth: 'var(--radix-dropdown-menu-trigger-width)' }}
          >
            {selectedCustomers.length === 1 && (
              <DropdownMenu.Item className="dropdown-item" onSelect={handleEditSelected}>
                Edit
              </DropdownMenu.Item>
            )}
            <DropdownMenu.Item
              className="dropdown-item dropdown-item--danger"
              onSelect={() => setDeleteConfirmOpen(true)}
            >
              Delete
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    );
  } else if (!selectedCustomerUuid && hasCustomers && isWorkspaceAdmin && !creatingCustomer) {
    headerActions = (
      <>
        <HeaderSearchControl
          value={customerSearchQuery}
          onChange={setCustomerSearchQuery}
          showDatabaseAction={showDatabaseSearchAction}
          onSearchDatabase={() => setDatabaseSearchTrigger((value) => value + 1)}
          searchingDatabase={searchingDatabase}
        />
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <Button type="button" variant="outline" size="sm">
              Import
            </Button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content className="dropdown-content" align="end" sideOffset={6}>
              <DropdownMenu.Item
                className="dropdown-item"
                onSelect={() => csvFileInputRef.current?.click()}
              >
                CSV
              </DropdownMenu.Item>
              <DropdownMenu.Item
                className="dropdown-item"
                onSelect={() => {
                  savePaneWidth();
                  setImportArchonOpen(true);
                  setImportPanel(null);
                  setImportAccepted([]);
                  setImportCreatedUuid(null);
                  setImportCreateInProgress(false);
                }}
              >
                Document
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
        <Button
          type="button"
          variant="primary"
          size="sm"
          onClick={() => {
            navigate(`${customersBasePath}/create`);
            setFormState({ submitting: false, canSubmit: false, saveSucceeded: false });
          }}
        >
          New Customer
        </Button>
      </>
    );
  } else if (!selectedCustomerUuid && hasCustomers && !creatingCustomer) {
    headerActions = (
      <HeaderSearchControl
        value={customerSearchQuery}
        onChange={setCustomerSearchQuery}
        showDatabaseAction={showDatabaseSearchAction}
        onSearchDatabase={() => setDatabaseSearchTrigger((value) => value + 1)}
        searchingDatabase={searchingDatabase}
      />
    );
  }

  if (isLoadingWorkspace) {
    return null;
  }

  const leftNav = (
    <AppNav
      selected="customers"
      isWorkspaceAdmin={isWorkspaceAdmin}
      onAdministrationClick={() => navigate('/administration')}
    />
  );

  const main = (
    <MainContentShell
      title={
        creatingCustomer
          ? 'Create new customer'
          : selectedCustomerUuid && selectedCustomerName
            ? detailEditing
              ? `Edit ${selectedCustomerName}`
              : selectedCustomerName
            : 'Customers'
      }
      actions={headerActions}
    >
      {selectedCustomerUuid ? (
        <CustomerDetailPage
          customerUuid={selectedCustomerUuid}
          canEdit={isWorkspaceAdmin}
          editing={detailEditing}
          onEndEdit={handleEndEdit}
          onStartEdit={handleStartEdit}
          formId="customer-edit-form"
          hideActions
          onFormStateChange={handleFormStateChange}
          onSaveSucceeded={handleEditSaveSucceeded}
          onCustomerNameChange={setSelectedCustomerName}
          pendingAttributeDraft={pendingAttributeDraft}
          onPendingAttributeConsumed={handlePendingAttributeConsumed}
          deletedAttributeSignal={deletedAttributeSignal}
          reloadSignal={detailReloadSignal}
        />
      ) : (
        <CustomerListPage
          canEdit={isWorkspaceAdmin}
          creating={creatingCustomer}
          formId="customer-create-form"
          hideActions
          onFormStateChange={handleFormStateChange}
          refreshKey={refreshKey}
          onSelectionChange={setSelectedCustomers}
          onCreateStart={() => {
            navigate(`${customersBasePath}/create`);
            setFormState({ submitting: false, canSubmit: false, saveSucceeded: false });
          }}
          onCreateCancel={handleCreateCancel}
          onSelectCustomer={(uuid) => navigate(`${customersBasePath}/${uuid}`)}
          onCustomerCountChange={(count, loading) => {
            if (!loading) {
              setHasCustomers(count > 0);
            }
          }}
          searchQuery={customerSearchQuery}
          databaseSearchTrigger={databaseSearchTrigger}
          onSearchUiStateChange={(state) => {
            setShowDatabaseSearchAction(state.showDatabaseAction);
            setSearchingDatabase(state.searchingDatabase);
          }}
          pendingAttributeDraft={pendingAttributeDraft}
          onPendingAttributeConsumed={handlePendingAttributeConsumed}
          deletedAttributeSignal={deletedAttributeSignal}
          onImportCsv={() => csvFileInputRef.current?.click()}
          onImportArchon={() => {
            savePaneWidth();
            setImportArchonOpen(true);
          }}
        />
      )}
    </MainContentShell>
  );

  return (
    <>
      <AppShell leftNav={leftNav} main={main} rightRailItems={rightRailItems} />

      <ConfirmDeleteDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title={`Delete ${
          selectedCustomers.length === 1 ? 'customer' : `${selectedCustomers.length} customers`
        }`}
        confirmKeyword={
          selectedCustomers.length === 1
            ? (selectedCustomers[0]!.email ?? selectedCustomers[0]!.name)
            : 'Yes'
        }
        matchMode={selectedCustomers.length === 1 ? 'exact' : 'caseInsensitive'}
        description={
          <>
            This action cannot be undone. The following
            {selectedCustomers.length === 1 ? ' customer' : ' customers'} will be permanently
            deleted:
            <ul className="customer-delete-modal__email-list">
              {selectedCustomers.map((c) => (
                <li key={c.uuid} className="customer-delete-modal__email-item">
                  {c.email ? `${c.name} (${c.email})` : c.name}
                </li>
              ))}
            </ul>
          </>
        }
        isPending={deleteInProgress}
        onConfirm={handleBulkDelete}
      />
      <Dialog.Root
        open={cancelExtractionConfirmOpen}
        onOpenChange={(open) => {
          if (!open) setCancelExtractionConfirmOpen(false);
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="pi-dialog-overlay" />
          <Dialog.Content className="pi-dialog-content">
            <div className="pi-modal__header">
              <Dialog.Title className="pi-modal__title">Discard extraction results?</Dialog.Title>
              <Dialog.Close asChild>
                <button className="pi-modal__close" aria-label="Close">
                  ×
                </button>
              </Dialog.Close>
            </div>
            <Dialog.Description className="pi-modal__body-text">
              The extracted values have not been applied. Cancelling will discard them.
            </Dialog.Description>
            <div className="pi-modal__actions">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setCancelExtractionConfirmOpen(false)}
              >
                Keep reviewing
              </Button>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={() => {
                  setCancelExtractionConfirmOpen(false);
                  setExtractionPanel(null);
                  setPendingExtractionAccepted([]);
                }}
              >
                Discard
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
      <Dialog.Root
        open={importCancelConfirmOpen}
        onOpenChange={(open) => {
          if (!open) setImportCancelConfirmOpen(false);
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="pi-dialog-overlay" />
          <Dialog.Content className="pi-dialog-content">
            <div className="pi-modal__header">
              <Dialog.Title className="pi-modal__title">Discard import?</Dialog.Title>
              <Dialog.Close asChild>
                <button className="pi-modal__close" aria-label="Close">
                  ×
                </button>
              </Dialog.Close>
            </div>
            <Dialog.Description className="pi-modal__body-text">
              The extracted values have not been saved. Cancelling will discard them.
            </Dialog.Description>
            <div className="pi-modal__actions">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setImportCancelConfirmOpen(false)}
              >
                Keep reviewing
              </Button>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={() => {
                  setImportCancelConfirmOpen(false);
                  handleImportClose();
                }}
              >
                Discard
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Hidden file input for CSV upload */}
      <input
        ref={csvFileInputRef}
        type="file"
        accept=".csv,text/csv"
        style={{ display: 'none' }}
        onChange={(e) => handleCsvFileChange(e)}
      />
    </>
  );
}

/**
 * CustomersPage - customers entry point for the product module.
 *
 * Wrapped by ProtectedRoute in App.tsx.
 */
export function CustomersPage() {
  return <CustomersPageContent />;
}
